import type { MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import { ArrowRight } from 'lucide-react'
import { buttonVariants } from '#app/components/ui/button'
import { cn } from '#app/utils/misc'
import { siteConfig } from '#app/utils/constants/brand'
import { ROUTE_PATH as LOGIN_PATH } from '#app/routes/auth+/login'

export const ROUTE_PATH = '/guides/forward-email-to-slack-without-zapier' as const

const TITLE = 'How to Forward Email to Slack (Without Zapier) | InboxBridge'
const DESCRIPTION =
  'A practical tutorial for forwarding email to Slack without Zapier: create an InboxBridge bridge, build a serverless function, and post formatted Slack Block Kit messages with filtering.'
const URL = `${siteConfig.siteUrl}/guides/forward-email-to-slack-without-zapier`

// Pinned manually so dateModified only changes when the content actually does.
// Bump dateModified when you make substantive edits to the guide body.
const DATE_PUBLISHED = '2026-07-07'
const DATE_MODIFIED = '2026-07-07'

const ARTICLE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: 'How to Forward Email to Slack (Without Zapier)',
  description: DESCRIPTION,
  image: siteConfig.siteImageUrl,
  url: URL,
  datePublished: DATE_PUBLISHED,
  dateModified: DATE_MODIFIED,
  author: {
    '@type': 'Organization',
    name: siteConfig.siteTitle,
    url: siteConfig.siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: siteConfig.siteTitle,
    url: siteConfig.siteUrl,
    logo: { '@type': 'ImageObject', url: siteConfig.siteImageUrl },
  },
  mainEntityOfPage: { '@type': 'WebPage', '@id': URL },
}

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteConfig.siteUrl },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'How to forward email to Slack (without Zapier)',
      item: URL,
    },
  ],
}

export const meta: MetaFunction = () => {
  return [
    { title: TITLE },
    { name: 'description', content: DESCRIPTION },
    { tagName: 'link', rel: 'canonical', href: URL },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: URL },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:image', content: siteConfig.siteImageUrl },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { 'script:ld+json': ARTICLE_JSON_LD },
    { 'script:ld+json': BREADCRUMB_JSON_LD },
  ]
}

const codeBlockClass =
  'overflow-x-auto rounded-lg border border-border bg-secondary/40 p-4 text-sm text-muted-foreground'

export default function ForwardEmailToSlackGuide() {
  return (
    <main className="mx-auto flex w-full max-w-screen-md flex-col gap-8 px-6 py-20">
      <header className="flex flex-col gap-4">
        <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Guide
        </p>
        <h1 className="text-4xl font-bold tracking-tight text-primary md:text-5xl">
          How to Forward Email to Slack (Without Zapier)
        </h1>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Getting important email into Slack is one of those things that sounds trivial
          until you actually try it. You want customer replies, form submissions, vendor
          alerts, or monitoring notices to land in a channel — cleanly formatted, and
          without the noise of everything else in the inbox.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          Slack has a built-in way to do this, and Zapier is the usual paid fallback. Both
          work, but both have real limits. In this guide we will look at those options
          honestly, then build a better one: a small serverless function that receives email
          as JSON from InboxBridge and posts a formatted message to a Slack incoming webhook,
          with filtering and attachment handling included.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          The whole build works on the InboxBridge free plan. You do not need Zapier, and you
          do not need to run a mail server.
        </p>
      </header>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">In this guide</h2>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>Slack&apos;s built-in email-to-channel address and its limits</li>
          <li>Zapier&apos;s email parser and what it costs</li>
          <li>Creating a Slack incoming webhook</li>
          <li>Creating an InboxBridge bridge</li>
          <li>Writing a serverless function that posts to Slack</li>
          <li>Formatting sender, subject, and body into Slack blocks</li>
          <li>Handling attachment metadata</li>
          <li>Filtering by sender and subject</li>
          <li>Verifying the request signature</li>
        </ul>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          The common approaches (and where they fall short)
        </h2>

        <h3 className="text-xl font-semibold text-primary">
          Slack&apos;s built-in email-to-channel address
        </h3>
        <p className="leading-relaxed text-muted-foreground">
          Slack can generate an email address for a channel. On paid Slack plans, you can add
          the &ldquo;Send emails to Slack&rdquo; app to a channel, get an address like{' '}
          <code>your-channel-xyz@yourworkspace.slack.com</code>, and forward mail to it. Every
          message sent there shows up in the channel.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          It is fine for a quick, low-volume feed, but the limits show up fast:
        </p>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>
            It is a paid-plan feature. On the free Slack plan the channel email address is
            not available.
          </li>
          <li>
            You get no control over formatting. Slack renders the raw email more or less
            as-is, collapsed behind a preview. You cannot pick out the fields that matter.
          </li>
          <li>
            There is no filtering. Everything forwarded to the address lands in the channel,
            including signatures, footers, and quoted reply chains.
          </li>
          <li>
            There is no routing logic. You cannot send billing emails to one channel and
            support emails to another based on sender or subject.
          </li>
          <li>You cannot post structured content, buttons, or links built from the email.</li>
        </ul>

        <h3 className="text-xl font-semibold text-primary">Zapier&apos;s email parser</h3>
        <p className="leading-relaxed text-muted-foreground">
          The usual next step is Zapier. Its Email Parser gives you a mailbox, you highlight
          the parts of a sample email you care about, and a Zap posts them to Slack. It works,
          and for a non-technical team it can be the right call.
        </p>
        <p className="leading-relaxed text-muted-foreground">The tradeoffs are cost and brittleness:</p>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>
            Zapier bills per task, and every email is a task. Add a filter step or a
            formatter step and a single email can burn multiple tasks. A busy address eats
            into your monthly quota quickly, and higher volumes push you onto paid tiers.
          </li>
          <li>
            Template-based parsing is fragile. When a sender changes their email layout, the
            highlighted fields drift and your Zap starts posting empty or wrong values.
          </li>
          <li>
            You are formatting inside a Zap editor, not in code. Anything beyond simple field
            mapping — conditional formatting, Block Kit layouts, attachment logic — gets
            awkward.
          </li>
          <li>Debugging lives in Zapier&apos;s task history rather than your own logs.</li>
        </ul>

        <h3 className="text-xl font-semibold text-primary">The webhook approach</h3>
        <p className="leading-relaxed text-muted-foreground">
          The alternative is to treat inbound email like any other event in your stack. You
          give out an email address, and when a message arrives it is delivered to your code
          as a clean JSON webhook. You format and post to Slack in a few lines, filtering
          happens in code you control, and there is no per-task meter running.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          That is what InboxBridge does. It gives you an inbound address, parses each message,
          and sends the result to your endpoint as structured JSON — signed and retried. The
          rest of this guide builds exactly that.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">What we are building</h2>
        <p className="leading-relaxed text-muted-foreground">The flow is short:</p>
        <ol className="ml-5 flex list-decimal flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>Someone sends (or forwards) an email to your InboxBridge address</li>
          <li>InboxBridge parses it and POSTs JSON to your serverless function</li>
          <li>Your function decides whether the email should be forwarded</li>
          <li>If it should, your function formats it and posts to a Slack incoming webhook</li>
          <li>The message appears in your channel, cleanly formatted</li>
        </ol>
        <p className="leading-relaxed text-muted-foreground">
          We will deploy the function on Vercel because it pairs naturally with a Next.js or
          Node project, but the same code runs on any platform that can receive an HTTP POST —
          Cloudflare Workers, AWS Lambda, Netlify, or your own server.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Step 1: Create a Slack incoming webhook
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          An incoming webhook is a URL that posts a message to one specific channel. To create
          one:
        </p>
        <ol className="ml-5 flex list-decimal flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>
            Go to <code>api.slack.com/apps</code> and create a new app (choose &ldquo;From
            scratch&rdquo;) in your workspace.
          </li>
          <li>Open &ldquo;Incoming Webhooks&rdquo; and toggle it on.</li>
          <li>Click &ldquo;Add New Webhook to Workspace&rdquo; and pick the target channel.</li>
          <li>
            Copy the generated URL. It looks like{' '}
            <code>https://hooks.slack.com/services/T000/B000/XXXX</code>.
          </li>
        </ol>
        <p className="leading-relaxed text-muted-foreground">
          Treat that URL like a password — anyone who has it can post to your channel. Store it
          as an environment variable, not in your code:
        </p>
        <pre className={codeBlockClass}>{`SLACK_WEBHOOK_URL=https://hooks.slack.com/services/T000/B000/XXXX`}</pre>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Step 2: Create an InboxBridge bridge
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          A bridge connects an inbound email address to your webhook URL. Create one, and
          InboxBridge generates a unique address for it, in the form{' '}
          <code>abc123@webhook.inboxbridge.io</code>. Everything sent to that address is parsed
          and delivered to the endpoint you configure.
        </p>
        <p className="leading-relaxed text-muted-foreground">When you create the bridge you set:</p>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>
            <strong>Webhook URL</strong> — where deliveries are POSTed, for example{' '}
            <code>https://your-app.vercel.app/api/email-to-slack</code>.
          </li>
          <li>
            <strong>Method</strong> — <code>POST</code> by default.
          </li>
          <li>
            <strong>Signing secret</strong> — generated for you, and used to sign every
            delivery so you can verify it came from InboxBridge.
          </li>
        </ul>
        <p className="leading-relaxed text-muted-foreground">
          We do not have the function deployed yet, so leave the URL as a placeholder for now
          and come back to it. A useful pattern is one bridge per purpose: a dedicated address
          for, say, monitoring alerts keeps that feed cleanly separated from customer mail — a
          coarse form of filtering before a single line of code runs.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Step 3: Receive the webhook
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          When an email arrives, InboxBridge sends your function a JSON body that looks like
          this:
        </p>
        <pre className={codeBlockClass}>
          {`{
  "id": "9b2c1f7a4e",
  "receivedAt": "2026-07-07T15:04:22.000Z",
  "from": { "email": "jordan@acme.com", "name": "Jordan Lee" },
  "to": [{ "email": "abc123@webhook.inboxbridge.io", "name": "" }],
  "cc": [],
  "bcc": [],
  "replyTo": null,
  "subject": "Question about invoice #1234",
  "date": "2026-07-07T15:04:00.000Z",
  "textBody": "Hi, can someone look into invoice #1234?",
  "htmlBody": "<p>Hi, can someone look into invoice #1234?</p>",
  "attachments": []
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Notice the shape: <code>from</code>, <code>to</code>, <code>cc</code>, and{' '}
          <code>bcc</code> are objects with an <code>email</code> and a <code>name</code>, not
          raw header strings, and the body is split into <code>textBody</code> and{' '}
          <code>htmlBody</code>. That makes formatting straightforward. Here is a first version
          of the function that just receives and logs:
        </p>
        <pre className={codeBlockClass}>
          {`// app/api/email-to-slack/route.ts (Next.js App Router on Vercel)

import { NextResponse } from "next/server";

export const runtime = "nodejs";

export async function POST(request: Request) {
  const email = await request.json();

  console.log("Inbound email:", {
    from: email.from.email,
    subject: email.subject,
  });

  return NextResponse.json({ ok: true });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Return a <code>2xx</code> quickly. InboxBridge treats a non-2xx or a timeout as a
          failed delivery and will retry it, which is exactly what you want when something is
          genuinely broken — but not something to trigger by doing slow work in the handler.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Step 4: Format the email into Slack blocks
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Slack&apos;s Block Kit lets you build a structured message instead of a wall of text.
          We will build a header with the subject, a context line with the sender, and a
          section with the body. Two details matter: a Slack section&apos;s text is capped at
          3000 characters, so truncate long bodies, and prefer <code>textBody</code> over the
          HTML version for a clean read.
        </p>
        <pre className={codeBlockClass}>
          {`// lib/format-slack-message.ts

type InboundEmail = {
  id: string;
  from: { email: string; name: string };
  subject: string;
  textBody: string | null;
  htmlBody: string | null;
  attachments: Array<{
    name: string;
    contentType: string;
    contentLength: number;
    downloadUrl?: string;
  }>;
};

function truncate(text: string, max = 2900) {
  return text.length > max ? text.slice(0, max) + "\\n\\n… (truncated)" : text;
}

function humanSize(bytes: number) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " KB";
  return (bytes / 1024 / 1024).toFixed(1) + " MB";
}

export function formatSlackMessage(email: InboundEmail) {
  const senderName = email.from.name || email.from.email;
  const body = truncate(email.textBody || "(no text body)");

  const blocks: any[] = [
    {
      type: "header",
      text: { type: "plain_text", text: email.subject || "(no subject)" },
    },
    {
      type: "context",
      elements: [
        {
          type: "mrkdwn",
          text: \`*From:* \${senderName} <\${email.from.email}>\`,
        },
      ],
    },
    {
      type: "section",
      text: { type: "mrkdwn", text: body },
    },
  ];

  return { blocks };
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          The <code>header</code> block only accepts plain text, so the raw subject is safe
          there. Body text goes in an <code>mrkdwn</code> section. If you expect senders to
          include Slack-sensitive characters, escape <code>&amp;</code>, <code>&lt;</code>, and{' '}
          <code>&gt;</code> in user-supplied strings before adding them to <code>mrkdwn</code>{' '}
          fields.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Step 5: Handle attachment metadata
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          InboxBridge always includes an <code>attachments</code> array describing each file —
          its <code>name</code>, <code>contentType</code>, and <code>contentLength</code>. On
          paid plans, each entry also carries a secure, time-limited <code>downloadUrl</code>,
          because the file bytes are stored for you and fetched on demand. On the free plan you
          get the metadata but no download link, which is all Slack needs to show what came in.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          Add a block that lists attachments, linking the file name when a{' '}
          <code>downloadUrl</code> is present:
        </p>
        <pre className={codeBlockClass}>
          {`// add inside formatSlackMessage, before "return { blocks }"

if (email.attachments.length > 0) {
  const lines = email.attachments.map((a) => {
    const size = humanSize(a.contentLength);
    const label = \`\${a.name} (\${size})\`;
    // Link the name when the plan includes a signed downloadUrl.
    return a.downloadUrl ? \`• <\${a.downloadUrl}|\${label}>\` : \`• \${label}\`;
  });

  blocks.push({
    type: "section",
    text: {
      type: "mrkdwn",
      text: \`*Attachments*\\n\${lines.join("\\n")}\`,
    },
  });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Signed download links expire after your plan&apos;s retention window, so they are
          meant for prompt access from the channel, not as permanent storage. If you need to
          keep a file, fetch it from the <code>downloadUrl</code> and re-store it somewhere
          durable.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Step 6: Filter which emails get forwarded
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          InboxBridge forwards every message that hits a bridge, which is the right default —
          it keeps routing rules in your code, where they are versioned and testable, rather
          than buried in a dashboard. So filtering lives in your function: check the sender and
          subject, and return early for anything you do not want in Slack.
        </p>
        <pre className={codeBlockClass}>
          {`// lib/should-forward.ts

const ALLOWED_SENDER_DOMAINS = ["acme.com", "billing.acme.com"];
const SUBJECT_KEYWORDS = ["invoice", "urgent", "outage"];

export function shouldForward(email: {
  from: { email: string };
  subject: string;
}) {
  const senderDomain = email.from.email.split("@")[1]?.toLowerCase() ?? "";
  const domainOk = ALLOWED_SENDER_DOMAINS.includes(senderDomain);

  const subject = (email.subject || "").toLowerCase();
  const subjectOk = SUBJECT_KEYWORDS.some((k) => subject.includes(k));

  // Forward only mail from a trusted domain that also matches a keyword.
  return domainOk && subjectOk;
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Tune the logic to your needs — an allowlist of exact senders, a subject regex, or a
          rule that only forwards mail addressed to a particular alias. The point is that it is
          plain code: easy to read, easy to test, and free to run. When an email does not match,
          return <code>200</code> so InboxBridge marks the delivery successful and does not
          retry it.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Step 7: Verify the request came from InboxBridge
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Your endpoint is public, so confirm each request is genuine before posting to Slack.
          InboxBridge signs every delivery. Each request carries an{' '}
          <code>X-InboxBridge-Signature</code> header in the form <code>sha256=…</code> and an{' '}
          <code>X-InboxBridge-Timestamp</code> header. The signature is an HMAC-SHA256 of the
          raw request body, keyed with your bridge&apos;s signing secret.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          The quickest way is our open-source package,{' '}
          <a
            href="https://www.npmjs.com/package/inbound-email-webhook"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            inbound-email-webhook
          </a>
          . It does the same check with a constant-time compare and returns a typed email, so
          it replaces the whole helper below with one call:
        </p>
        <pre className={codeBlockClass}>{`npm install inbound-email-webhook`}</pre>
        <pre className={codeBlockClass}>
          {`// app/api/email-to-slack/route.ts

import { verifyRequest } from "inbound-email-webhook/next";

export const runtime = "nodejs";

export async function POST(request: Request) {
  const result = await verifyRequest(request, {
    secret: process.env.INBOXBRIDGE_SIGNING_SECRET!,
  });

  if (!result.ok) return result.response;

  const email = result.email; // typed InboundEmail
  // filter, format, and post to Slack
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Prefer not to add a dependency? Verify by hand. Read the body as text and verify
          before you call <code>JSON.parse</code>, because the HMAC is computed over the exact
          bytes that were sent:
        </p>
        <pre className={codeBlockClass}>
          {`// lib/verify-signature.ts

import crypto from "crypto";

export function verifySignature(rawBody: string, signatureHeader: string) {
  const expected =
    "sha256=" +
    crypto
      .createHmac("sha256", process.env.INBOXBRIDGE_SIGNING_SECRET!)
      .update(rawBody)
      .digest("hex");

  const a = Buffer.from(signatureHeader);
  const b = Buffer.from(expected);
  return a.length === b.length && crypto.timingSafeEqual(a, b);
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Always compare with a timing-safe function, never <code>===</code>. This example uses
          Node&apos;s <code>crypto</code> module, so keep the route on the Node.js runtime; on
          an Edge runtime, use Web Crypto instead.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Full example</h2>
        <p className="leading-relaxed text-muted-foreground">
          Putting it together: verify the signature, filter, format, and post to Slack.
        </p>
        <pre className={codeBlockClass}>
          {`// app/api/email-to-slack/route.ts

import { NextResponse } from "next/server";
import { verifySignature } from "@/lib/verify-signature";
import { shouldForward } from "@/lib/should-forward";
import { formatSlackMessage } from "@/lib/format-slack-message";

export const runtime = "nodejs";

export async function POST(request: Request) {
  const rawBody = await request.text();
  const signature = request.headers.get("x-inboxbridge-signature") ?? "";

  if (!verifySignature(rawBody, signature)) {
    return NextResponse.json({ error: "Invalid signature" }, { status: 401 });
  }

  const email = JSON.parse(rawBody);

  // Filtering: only forward mail we care about.
  if (!shouldForward(email)) {
    return NextResponse.json({ ok: true, skipped: true });
  }

  const message = formatSlackMessage(email);

  const res = await fetch(process.env.SLACK_WEBHOOK_URL!, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(message),
  });

  if (!res.ok) {
    // Return non-2xx so InboxBridge retries with backoff.
    return NextResponse.json({ error: "Slack post failed" }, { status: 502 });
  }

  return NextResponse.json({ ok: true });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          A few things worth calling out in that handler:
        </p>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>It verifies the signature before doing anything else.</li>
          <li>It filters in code, returning a successful, non-retried response for skips.</li>
          <li>
            When Slack itself fails, it returns <code>502</code> so InboxBridge retries the
            delivery with exponential backoff instead of silently dropping the message.
          </li>
        </ul>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Avoid duplicate messages</h2>
        <p className="leading-relaxed text-muted-foreground">
          Because failed deliveries are retried, the same email can occasionally arrive twice —
          for instance if your function posts to Slack but times out before responding. Every
          payload includes a stable <code>id</code>, so if a double-post would be a problem you
          can record recently seen ids (in a small KV store or your database) and skip anything
          you have already handled. For a low-volume alert channel this is often unnecessary,
          but it is worth knowing the <code>id</code> is there.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Test it end to end</h2>
        <p className="leading-relaxed text-muted-foreground">Once the function is deployed:</p>
        <ol className="ml-5 flex list-decimal flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>
            Paste the deployed URL into your bridge, for example{' '}
            <code>https://your-app.vercel.app/api/email-to-slack</code>.
          </li>
          <li>
            Send a test email to your bridge address that matches your filter (a trusted sender
            and a keyword subject).
          </li>
          <li>Watch the formatted message appear in your Slack channel.</li>
          <li>
            Open the bridge&apos;s delivery log to confirm a <code>200</code> response, with the
            status code and response time recorded for that attempt.
          </li>
        </ol>
        <p className="leading-relaxed text-muted-foreground">
          Testing locally? InboxBridge cannot reach <code>localhost</code>, so use a tunnel like
          ngrok or Cloudflare Tunnel and point the bridge at the public forwarding URL while you
          iterate.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          That delivery log is the quiet advantage over both alternatives. Every attempt is
          recorded with its response code and duration, failures are retried with backoff, and
          on paid plans you can replay any past message to Slack after fixing your function —
          none of which Slack&apos;s built-in address or a fire-and-forget Zap gives you.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Wrapping up</h2>
        <p className="leading-relaxed text-muted-foreground">
          Slack&apos;s built-in email address is the fastest way to get mail into a channel, but
          it is paid-plan only, unformatted, and unfiltered. Zapier adds flexibility at a
          per-task cost that climbs with volume. Forwarding email to Slack through a webhook
          gives you full control over formatting and filtering, keeps the logic in code you own,
          adds signed and retried delivery, and runs comfortably on the free tier.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          Create a bridge, deploy the function, and send yourself a test email. You will have
          clean, filtered email landing in Slack in a few minutes — no Zapier subscription and no
          mail server.
        </p>
      </section>

      <div className="flex flex-wrap gap-3">
        <Link
          to={LOGIN_PATH}
          className={cn(buttonVariants({ size: 'lg' }), 'gap-2 font-semibold')}>
          Create your first bridge
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/email-to-webhook"
          className={cn(buttonVariants({ variant: 'outline', size: 'lg' }))}>
          Email to webhook overview
        </Link>
      </div>

      <section className="mt-8 border-t border-border/40 pt-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary">
          Related
        </h2>
        <ul className="flex flex-col gap-2 text-base text-muted-foreground">
          <li>
            <Link
              to="/guides/receive-inbound-email-as-json-nextjs"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Receive inbound email as JSON in Next.js
            </Link>
            . The framework-specific companion, with route handler and payload details.
          </li>
          <li>
            <Link
              to="/guides/inbound-email-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Inbound email webhook tutorial
            </Link>
            . The five-minute version that is not tied to a framework.
          </li>
          <li>
            <Link
              to="/email-to-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Email to webhook overview
            </Link>
            . What the service does and what payloads look like.
          </li>
          <li>
            <Link
              to="/"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              InboxBridge home
            </Link>
            . An overview of turning inbound email into webhooks.
          </li>
        </ul>
      </section>
    </main>
  )
}
